import { useAtomValue } from 'jotai';
import { overlay } from 'overlay-kit';
import { useCallback, useRef, useState } from 'react';

import { askSynclyFilterAtom } from '../atoms/ask-syncly-filter-atom';
import { useChat } from '../hooks/use-chat';
import { ChatHeader } from './chat-header';
import { ChatInput } from './chat-input';
import { CollapsedChatSidebar } from './collapsed-chat-sidebar';
import { NewSessionAlert } from './new-session-alert';
import { ResizeHandle } from './resize-handle';

import { createNewChatSession } from '@/entities/ask-syncly/api';
import { AskMode, QuestionAnswerBlockStatus } from '@/entities/ask-syncly/ask-syncly-constants';
import { Stage } from '@/shared/components/stage/stage';
import { useSelectedSavedQuery } from '@/shared/hooks/use-selected-saved-query';
import { useSharedDateRange } from '@/shared/hooks/use-shared-date-range';
import { useWorkspaceId } from '@/shared/hooks/use-workspace-id';
import { cn } from '@/shared/utils/cn';
import { Loader } from 'lucide-react';
import type { SidebarState } from '../atoms/sidebar-state-atom';
import { useDeepResearch } from '../hooks';
import { ChatDisplay } from './chat-display';
import { TemplateQuestionsSection } from './template-questions-section';

interface ChatSidebarProps {
  className?: string;
  sidebarState: SidebarState;
  onToggle: () => void;
  onSidebarStateChange?: (newState: SidebarState) => void;
}

export function ChatSidebar({
  className,
  sidebarState,
  onToggle,
  onSidebarStateChange,
}: ChatSidebarProps) {
  const [mode, setMode] = useState<AskMode>(AskMode.GENERAL);
  const { workspaceId } = useWorkspaceId();
  const { query: selectedQuery } = useSelectedSavedQuery();
  const { dateRange } = useSharedDateRange();
  const askSynclyFilter = useAtomValue(askSynclyFilterAtom);
  const resolvedDateRange = askSynclyFilter?.dateRange ?? dateRange;
  // const toAskSynclyPage = useAskSynclyNavigation();

  const {
    data: chatSession,
    refetch: refetchChatSession,
    isLoading: isLoadingChatSession,
  } = useChat({
    queryId: selectedQuery.id,
    workspaceId,
    mode,
  });

  const {
    data: currentAnswerBlock,
    submit,
    loading: askLoading,
    clear: clearDeepResearch,
  } = useDeepResearch({
    sessionId: chatSession?.id,
    queryId: selectedQuery.id,
    dateRange: resolvedDateRange,
    onDone: async () => {
      await refetchChatSession();
      clearDeepResearch();
    },
  });

  const handleNewSession = useCallback(async () => {
    await new Promise<void>((resolve, reject) => {
      overlay.open(({ isOpen, close }) => {
        return (
          <NewSessionAlert
            isOpen={isOpen}
            message="Your current conversation will be lost and cannot be recovered. Conversation history will be available soon."
            onConfirm={async () => {
              await createNewChatSession(workspaceId, selectedQuery.id, mode);
              await useChat.refetch(workspaceId, selectedQuery.id, mode);
              clearDeepResearch();
              close();
              resolve();
            }}
            onCancel={() => {
              close();
              reject();
            }}
          />
        );
      });
    });
  }, [clearDeepResearch, mode, selectedQuery.id, workspaceId]);

  // Resize functionality
  const isResizing = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isResizing.current = true;
      startX.current = e.clientX;
      startWidth.current = sidebarState.width;

      const onMouseMove = (e: MouseEvent) => {
        if (!isResizing.current) return;

        const deltaX = startX.current - e.clientX;
        const newWidth = startWidth.current + deltaX;
        const constrainedWidth = Math.max(
          sidebarState.minWidth,
          Math.min(sidebarState.maxWidth, newWidth),
        );

        onSidebarStateChange?.({
          ...sidebarState,
          width: constrainedWidth,
        });
      };

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        document.body.style.userSelect = '';
        isResizing.current = false;
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
      // Prevent text selection during resize
      document.body.style.userSelect = 'none';
    },
    [sidebarState, onSidebarStateChange],
  );

  const hasPreviousAnswer = (chatSession?.blocks && chatSession?.blocks?.length > 0) || false;

  const isModeChanging = useRef(false);
  const onModeChange = useCallback(
    async (mode: AskMode) => {
      if (isModeChanging.current) return;
      isModeChanging.current = true;
      try {
        if (isLoadingChatSession || askLoading) {
          await handleNewSession();
        }
        setMode(mode);
      } finally {
        isModeChanging.current = false;
      }
    },
    [askLoading, handleNewSession, isLoadingChatSession],
  );

  if (!sidebarState.isOpen) {
    return <CollapsedChatSidebar onToggle={onToggle} />;
  }

  return (
    <>
      <div className="h-screen" style={{ width: `${sidebarState.width}px` }} />
      <div
        className={cn(
          'bg-muted fixed top-0 right-0 z-20 flex h-screen flex-col shadow-[-2px_0px_4px_-1px_#00000026]',
          className,
        )}
        style={{ width: `${sidebarState.width}px` }}
      >
        <ResizeHandle onMouseDown={handleResizeStart} />
        <ChatHeader
          className="flex-0"
          onNewSession={handleNewSession}
          onToggle={onToggle}
          // onExpand={() => {
          //   toAskSynclyPage({ queryId: selectedQuery.id, dateRange: resolvedDateRange });
          // }}
        />
        <Stage className="flex flex-1 overflow-y-auto">
          <Stage.Scene status={isLoadingChatSession}>
            <div className="flex w-full flex-1 flex-col items-center justify-center p-6">
              <Loader className="size-4 animate-spin" />
            </div>
          </Stage.Scene>
          <Stage.Scene status={!chatSession?.blocks && !currentAnswerBlock}>
            <div className="flex flex-1 flex-col justify-center p-6">
              <TemplateQuestionsSection
                mode={mode}
                onQuestionClick={submit}
                queryId={selectedQuery.id}
                workspaceId={workspaceId}
              />
            </div>
          </Stage.Scene>
          <Stage.Scene default>
            <ChatDisplay
              mode={mode}
              className="flex flex-1 flex-col overflow-y-auto"
              session={chatSession}
              currentAnswerBlock={currentAnswerBlock}
              isChatSessionLoading={isLoadingChatSession}
            />
          </Stage.Scene>
        </Stage>

        <ChatInput
          className="flex-0 border-t pt-4"
          onSubmit={submit}
          loading={
            currentAnswerBlock !== null &&
            currentAnswerBlock?.status !== QuestionAnswerBlockStatus.COMPLETED
          }
          hasPreviousAnswer={hasPreviousAnswer}
          mode={mode}
          onModeChange={onModeChange}
        />
      </div>
    </>
  );
}
